import React, { useCallback, useEffect, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import EsgConfirmDialog from './EsgConfirmDialog';
import {
  caseScoreOptions,
  chairDraftFrom,
  formatDateTime,
  formatSpent,
  squadDraftFrom,
  statusBadgeClass,
  statusLabel,
  taskDraftFrom,
  taskKeyLabel,
  taskPayload,
  taskReady,
} from './esgEvaluationUi';

const COMMENT_MAX = 500;
const GENERIC_ERROR = "Couldn't complete that - please try again.";

// When the meeting really took place: the chair's START and CLOSE in the LMS ('10:05 - 11:20 (75 min)'). Not started: a dash.
export function formatHeld(startedAt, closedAt, autoClosed) {
  if (!startedAt) return '—';
  const clock = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const day = new Date(startedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  if (!closedAt) return `${day}, ${clock(startedAt)} - not closed yet`;
  const minutes = Math.max(0, Math.round((new Date(closedAt) - new Date(startedAt)) / 60000));
  return `${day}, ${clock(startedAt)} - ${clock(closedAt)} (${minutes} min)${autoClosed ? ' - closed automatically' : ''}`;
}

function RatingButtons({ value, min, max, disabled, onChange, label }) {
  const numbers = [];
  for (let n = min; n <= max; n += 1) numbers.push(n);
  return (
    <div className="rating-buttons-container" role="group" aria-label={label}>
      {numbers.map((n) => (
        <button
          key={n}
          type="button"
          className={`rating-button ${value === n ? 'selected' : ''}`}
          disabled={disabled}
          aria-pressed={value === n}
          onClick={() => onChange(n)}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

// descriptions: {label: text} - shown as the hover text of each option.
function Chips({ words, values, negative, disabled, onToggle, descriptions = {} }) {
  return (
    <div className="keyword-chips-grid">
      {words.map((word) => {
        const button = (
          <button
            key={word}
            type="button"
            disabled={disabled}
            className={`keyword-chip ${negative ? 'coaching' : 'strength'} ${values.includes(word) ? 'selected' : ''}`}
            onClick={() => onToggle(word)}
          >
            {word}
          </button>
        );
        // The description shows as a tooltip on hover / keyboard focus (see .esg-tip in styles.css).
        return descriptions[word]
          ? <span key={word} className="esg-tip" data-tip={descriptions[word]}>{button}</span>
          : button;
      })}
    </div>
  );
}

// Long text (a summary, a transcript, a task) scrolls inside its own box instead of pushing the form far down the page.
const SCROLL_TEXT = { whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' };

const CONTEXT_LABEL_STYLE = {
  fontSize: '10px',
  fontWeight: 700,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '3px',
};

const NOTE_STYLE = { background: '#fffbeb', border: '1px solid #f59e0b', color: '#92400e', padding: '10px 14px', borderRadius: '10px', fontSize: '13px', marginBottom: '12px' };
const DONE_STYLE = { background: '#ecfdf5', border: '1.5px solid #10b981', color: '#065f46', padding: '10px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, marginBottom: '12px' };

function StepStatus({ submittedAt, available, waitingText }) {
  if (submittedAt) return <div style={DONE_STYLE}>{`✓ Submitted on ${formatDateTime(submittedAt)}. It is final.`}</div>;
  if (!available) return <div role="note" style={NOTE_STYLE}>{waitingText}</div>;
  return null;
}

// One of the two rating steps (the squad as a whole, the chair): chips, a 1-10 rating and a comment, confirmed once.
function RatingStep({ kind, heading, badge, available, waitingText, submittedAt, revised, draft, onChange, words, hints, ratingRange, canEdit, canReopen, busy, onSubmit, onReopen }) {
  const locked = Boolean(submittedAt) || !available || !canEdit;
  const toggle = (field, other, word) => {
    const has = draft[field].includes(word);
    onChange({
      [field]: has ? draft[field].filter((w) => w !== word) : [...draft[field], word],
      // A tag cannot be both a strength and a coaching area.
      ...(!has ? { [other]: draft[other].filter((w) => w !== word) } : {}),
    });
  };

  return (
    <div className="form-card-container" data-step={kind}>
      <div className="student-card-header">
        <span className="student-card-name">{heading}</span>
        <span>
          {revised && <span className="role-badge revised esg-evaluation-workspace__span-1">Revised</span>}
          <span className={`role-badge ${kind === 'chair' ? 'chair' : 'auditor'}`}>{badge}</span>
        </span>
      </div>

      <StepStatus submittedAt={submittedAt} available={available} waitingText={waitingText} />

      <div className="form-field-group">
        <label>Strengths Demonstrated (Tap to Select)</label>
        <Chips words={words.strengths || []} values={draft.strengths} descriptions={hints} disabled={locked} onToggle={(w) => toggle('strengths', 'coaching', w)} />
      </div>
      <div className="form-field-group">
        <label>Coaching Required (Tap to Select)</label>
        <Chips negative words={words.coaching || []} values={draft.coaching} descriptions={hints} disabled={locked} onToggle={(w) => toggle('coaching', 'strengths', w)} />
      </div>
      <div className="form-field-group">
        <label>Live Rating (1–10)</label>
        <RatingButtons label={`${heading} rating`} value={draft.rating} min={ratingRange.min} max={ratingRange.max} disabled={locked} onChange={(n) => onChange({ rating: n })} />
        <span className="form-helper-text">{`Rate from ${ratingRange.min} (poor) to ${ratingRange.max} (outstanding).`}</span>
      </div>
      <div className="form-field-group">
        <label htmlFor={`esg-${kind}-comment`}>Feedback / Comments</label>
        <textarea
          id={`esg-${kind}-comment`}
          className="form-control-textarea"
          placeholder="Optional comment (max 500 chars)"
          maxLength={COMMENT_MAX}
          disabled={locked}
          value={draft.comment}
          onChange={(e) => onChange({ comment: e.target.value })}
        />
        <span className="char-counter">{`${draft.comment.length} / ${COMMENT_MAX} characters`}</span>
      </div>

      {!submittedAt && (
        <button type="button" className="esg-btn esg-btn-primary w-100" disabled={locked || busy || draft.rating === null} onClick={onSubmit}>
          {`Submit ${kind === 'chair' ? 'chair' : 'squad'} evaluation`}
        </button>
      )}
      {submittedAt && canReopen && (
        <button type="button" className="esg-btn esg-btn-secondary" disabled={busy} onClick={onReopen}>Reopen this step</button>
      )}
    </div>
  );
}

// One member's individual task: waiting for their submission, ready to rate, rated, or marked Not submitted.
function TaskCard({ member, draft, onChange, criteria, canEdit, canReopen, busy, onSubmit, onNotSubmitted, onReopen }) {
  const state = member.task_state;
  const rows = member.task_evaluations || [];
  const done = state === 'evaluated' || state === 'not_submitted';
  const locked = !canEdit || state !== 'ready';

  return (
    <div className="form-card-container esg-evaluation-workspace__div-2" data-task-state={state}>
      <div className="student-card-header">
        <span className="student-card-name">{member.name}</span>
        <span>
          {member.present === false && <span className="role-badge revised esg-evaluation-workspace__span-1">ABSENT</span>}
          <span className={`role-badge ${member.is_chair ? 'chair' : 'delegate'}`}>{member.is_chair ? 'CHAIR' : 'MEMBER'}</span>
        </span>
      </div>

      {member.present === false && member.task?.submitted && (
        <div role="note" style={NOTE_STYLE}>
          <strong>Absent from this session</strong>, but submitted the individual task (tasks are submitted before the session). Review it before confirming.
        </div>
      )}

      {member.task?.submitted && (
        <div className="form-field-group">
          <label>Submission</label>
          <p style={SCROLL_TEXT}>{member.task.body}</p>
        </div>
      )}

      {state === 'waiting' && (
        <>
          <div role="note" style={NOTE_STYLE}>
            {member.task?.submitted
              ? `${member.name} has written the task but has not submitted this council for audit yet. You can rate it once they do.`
              : `Waiting for ${member.name} to submit this council for audit.`}
          </div>
          {canEdit && (
            <button type="button" className="esg-btn esg-btn-secondary" disabled={busy} onClick={onNotSubmitted}>
              Mark as Not submitted (scores 0)
            </button>
          )}
        </>
      )}

      {state === 'ready' && member.task_keys.map((key) => {
        const row = draft[key] || { confirmed: null, rating: null, note: '' };
        const scored = key !== 'task';
        return (
          <div className="form-field-group" key={key}>
            <label>{`${taskKeyLabel(key)} (Tap to Select)`}</label>
            <div className="keyword-chips-grid">
              <button type="button" className={`keyword-chip strength ${row.confirmed === true ? 'selected' : ''}`} disabled={locked} aria-pressed={row.confirmed === true} onClick={() => onChange(key, { confirmed: true })}>
                Confirm
              </button>
              <button type="button" className={`keyword-chip coaching ${row.confirmed === false ? 'selected' : ''}`} disabled={locked} aria-pressed={row.confirmed === false} onClick={() => onChange(key, { confirmed: false, rating: null })}>
                Not confirmed
              </button>
            </div>
            {scored && row.confirmed === true && (
              <select
                className="form-control-select esg-evaluation-workspace__select-3"
                aria-label={`Score for ${member.name}, ${taskKeyLabel(key)}`}
                disabled={locked}
                value={row.rating ?? ''}
                onChange={(e) => onChange(key, { rating: e.target.value === '' ? null : Number(e.target.value) })}
              >
                <option value="">Score /10</option>
                {caseScoreOptions(criteria.task_rating).map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            )}
            <input
              className="form-control-input esg-evaluation-workspace__input-4"
              placeholder="Note (optional)"
              aria-label={`Note for ${member.name}, ${taskKeyLabel(key)}`}
              maxLength={300}
              disabled={locked}
              value={row.note}
              onChange={(e) => onChange(key, { note: e.target.value })}
            />
          </div>
        );
      })}

      {state === 'ready' && (
        <button type="button" className="esg-btn esg-btn-primary w-100" disabled={locked || busy || !taskReady(member.task_keys, draft)} onClick={onSubmit}>
          {`Submit ${member.name}'s task evaluation`}
        </button>
      )}

      {done && (
        <>
          {state === 'not_submitted' ? (
            <div style={DONE_STYLE}>✓ Marked as Not submitted — scores 0. It is final.</div>
          ) : (
            <div style={DONE_STYLE}>
              {'✓ Evaluated. It is final.'}
              {rows.map((t) => (
                <div key={t.task_key} className="esg-evaluation-workspace__div-5">
                  {`${taskKeyLabel(t.task_key)}: ${t.confirmed ? `confirmed${t.rating !== null && t.rating !== undefined ? ` · ${t.rating}/10` : ''}` : 'not confirmed'}${t.note ? ` — ${t.note}` : ''}`}
                </div>
              ))}
            </div>
          )}
          {canReopen && <button type="button" className="esg-btn esg-btn-secondary" disabled={busy} onClick={onReopen}>Reopen this step</button>}
        </>
      )}
    </div>
  );
}

// One squad + council evaluation in three independent steps - the squad as a whole, the chair, and each member's individual
// task - each confirmed on its own and then final (only a program administrator can reopen one). The squad and chair steps
// open once the chair has submitted the council for audit; a member's task opens once that member has.
export default function EsgEvaluationWorkspace({ squadId, council, backLabel = 'Back to squad', onBack, onChanged, onError, onSuccess }) {
  const [workspace, setWorkspace] = useState(null);
  const [squad, setSquad] = useState(null);
  const [chair, setChair] = useState(null);
  const [tasks, setTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ws = await mentorApi.getEsgEvaluationWorkspace(squadId, council);
      setWorkspace(ws);
      setSquad(squadDraftFrom(ws));
      setChair(chairDraftFrom(ws));
      setTasks(Object.fromEntries(ws.members.map((m) => [m.student_id, taskDraftFrom(m)])));
    } catch (err) {
      onError?.("Couldn't load this evaluation - try again.");
    } finally {
      setLoading(false);
    }
  }, [squadId, council, onError]);

  useEffect(() => {
    load();
  }, [load]);

  const backButton = (
    <button type="button" className="btn-back-link" onClick={onBack}>
      {`← ${backLabel}`}
    </button>
  );

  if (loading && !workspace) {
    return (
      <div className="evaluation-workspace animate-fade-in esg-evaluation-workspace__div-6">
        {backButton}
        <p className="esg-evaluation-workspace__p-7">Loading evaluation...</p>
      </div>
    );
  }
  // The drafts are set one after another (outside React's batching), so wait until all of them exist.
  if (!workspace || !squad || !chair) {
    return (
      <div className="evaluation-workspace animate-fade-in esg-evaluation-workspace__div-6">
        {backButton}
        <div className="empty-state-card"><p className="empty-state-description">This evaluation could not be loaded.</p></div>
      </div>
    );
  }

  const evaluation = workspace.evaluation;
  const status = evaluation?.status || null;
  const criteria = workspace.criteria || {};
  const ratingRange = criteria.rating || { min: 1, max: 10 };
  const canEdit = workspace.can_edit;
  const canReopen = workspace.can_reopen;
  const steps = workspace.steps || {};
  const taskMembers = workspace.members.filter((m) => m.task_state !== 'not_applicable');
  const tasksDone = taskMembers.filter((m) => m.task_state === 'evaluated' || m.task_state === 'not_submitted').length;

  // Runs one step against the server, then reloads so every part shows its new state.
  const run = async (action, successMessage) => {
    setBusy(true);
    setFormError('');
    try {
      await action();
      setConfirm(null);
      await load();
      onChanged?.();
      onSuccess?.(successMessage);
    } catch (err) {
      setConfirm(null);
      const errors = err?.data?.errors;
      setFormError((errors && Object.values(errors).flat()[0]) || err?.data?.message || GENERIC_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const confirms = {
    squad: {
      title: 'Submit the squad evaluation?',
      label: 'Yes, submit',
      body: 'Once submitted it cannot be edited — only a program administrator can reopen it. The squad will see it in the LMS.',
      go: () => run(() => mentorApi.submitEsgSquadEvaluation(squadId, council, {
        session_rating: squad.rating, session_strengths: squad.strengths, session_coaching: squad.coaching, session_comment: squad.comment || null,
      }), 'Squad evaluation submitted. It is final.'),
    },
    chair: {
      title: 'Submit the chair evaluation?',
      label: 'Yes, submit',
      body: 'Once submitted it cannot be edited — only a program administrator can reopen it. The chair will see it in the LMS.',
      go: () => run(() => mentorApi.submitEsgChairEvaluation(squadId, council, {
        chair_rating: chair.rating, chair_strengths: chair.strengths, chair_coaching: chair.coaching, chair_comment: chair.comment || null,
      }), 'Chair evaluation submitted. It is final.'),
    },
  };

  const askTask = (member) => setConfirm({
    title: `Submit ${member.name}'s task evaluation?`,
    label: 'Yes, submit',
    body: 'Once submitted it cannot be edited — only a program administrator can reopen it. The score counts towards their certificate.',
    go: () => run(() => mentorApi.submitEsgTaskEvaluation(squadId, council, member.student_id, taskPayload(member.task_keys, tasks[member.student_id] || {})), `${member.name}'s task evaluation submitted. It is final.`),
  });
  const askNotSubmitted = (member) => setConfirm({
    title: `Mark ${member.name} as Not submitted?`,
    label: 'Yes, mark as Not submitted',
    body: 'Their individual task scores 0. If they submit this council for audit later, this mark is removed and you can rate their task.',
    go: () => run(() => mentorApi.submitEsgTaskEvaluation(squadId, council, member.student_id, { outcome: 'not_submitted' }), `${member.name} marked as Not submitted.`),
  });
  const askReopen = (title, part, studentId) => setConfirm({
    title,
    label: 'Yes, reopen',
    body: 'The squad stops seeing this step until it is submitted again.',
    go: () => run(() => mentorApi.reopenEsgEvaluationStep(squadId, council, { part, student_id: studentId }), 'Step reopened.'),
  });
  const ask = (kind) => setConfirm({ title: confirms[kind].title, label: confirms[kind].label, body: confirms[kind].body, go: confirms[kind].go });

  const scheduledAt = workspace.session.scheduled_at;
  const contextItems = [
    ['Date', scheduledAt ? scheduledAt.split('T')[0] : '—'],
    ['Time', scheduledAt ? new Date(scheduledAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '—'],
    [
      'Since previous council',
      workspace.session.days_since_previous === null || workspace.session.days_since_previous === undefined
        ? '—'
        : `${workspace.session.days_since_previous} ${workspace.session.days_since_previous === 1 ? 'day' : 'days'}`,
    ],
    ['Squad', workspace.squad.name],
    ['Level', workspace.squad.level?.sequence ? `Level ${workspace.squad.level.sequence}` : '—'],
    ['Mission', workspace.mission_number ? `Mission ${workspace.mission_number}` : '—'],
    ['Held', formatHeld(workspace.session.started_at, workspace.session.closed_at, workspace.session.auto_closed)],
    [
      'Online link',
      workspace.session.meeting_url && workspace.session.meeting_url.startsWith('https://')
        ? <a href={workspace.session.meeting_url} target="_blank" rel="noopener noreferrer">Join session</a>
        : '—',
    ],
  ];

  return (
    <div className="evaluation-workspace animate-fade-in esg-evaluation-workspace__div-8">
      {backButton}

      <div className="evaluation-sticky-header">
        <div className="sticky-header-container">
          <div className="sticky-header-meta">
            <h2 className="sticky-title">{`ESG Evaluation — Council ${council}`}</h2>
            <p className="sticky-subtitle">
              {[
                workspace.squad.name,
                workspace.squad.level?.sequence && `Level ${workspace.squad.level.sequence}`,
                workspace.mission_number && `Mission ${workspace.mission_number}`,
              ].filter(Boolean).join(' • ')}
            </p>
          </div>
          <span className={`status-badge status-${statusBadgeClass(status)}`}>{statusLabel(status)}</span>
        </div>
      </div>

      <div className="esg-evaluation-workspace__div-9">
        {contextItems.map(([label, value]) => (
          <div key={label}>
            <div style={CONTEXT_LABEL_STYLE}>{label}</div>
            <div className="esg-evaluation-workspace__div-10">{value}</div>
          </div>
        ))}
      </div>

      <div role="status" className="esg-evaluation-workspace__div-11">
        {`Squad ${steps.squad?.submitted_at ? '✓' : '○'}  ·  Chair ${workspace.chair ? (steps.chair?.submitted_at ? '✓' : '○') : '—'}  ·  Individual tasks ${tasksDone} of ${taskMembers.length}`}
      </div>

      {!workspace.session.started && (
        <div className="rejection-banner esg-evaluation-workspace__div-12">
          <strong>Not started.</strong> This council has not started yet, so it cannot be evaluated.
        </div>
      )}
      {formError && <div className="rejection-banner esg-evaluation-workspace__div-12" role="alert">{formError}</div>}

      <div className="form-section-header">
        <h3>Session Record</h3>
        <div className="section-divider"></div>
      </div>
      <div className="form-card-container">
        <div className="form-field-group">
          <label>Summary</label>
          <p style={SCROLL_TEXT}>{workspace.report?.summary || 'No summary has been logged yet.'}</p>
        </div>
        <div className="form-field-group">
          <label>Minutes</label>
          <p style={SCROLL_TEXT}>{workspace.report?.minutes || 'No minutes have been logged yet.'}</p>
        </div>
        <div className="students-table-card">
          <table className="students-table">
            <thead>
              <tr><th>Member</th><th>Present</th><th>Time on council</th><th>Self rating</th><th>Learning</th><th>Task submitted</th><th>Submitted for audit</th></tr>
            </thead>
            <tbody>
              {workspace.members.map((m) => (
                <tr key={m.student_id}>
                  <td>{m.name}{m.is_chair ? ' (Chair)' : ''}</td>
                  <td>{m.present === null ? '-' : m.present ? 'Yes' : 'No'}</td>
                  <td>{formatSpent(m.time_spent_seconds)}</td>
                  <td>{m.self_rating ?? '-'}</td>
                  <td>{m.self_learning || '-'}</td>
                  <td>{m.task?.submitted ? 'Yes' : 'No'}</td>
                  <td>{m.audit_submitted_at ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="form-section-header esg-evaluation-workspace__div-13">
        <h3>Squad Evaluation</h3>
        <div className="section-divider"></div>
      </div>
      <RatingStep
        kind="squad"
        heading={`Council ${council} session`}
        badge="SESSION"
        available={Boolean(steps.squad?.available)}
        waitingText="Opens once the chair has submitted this council for audit."
        submittedAt={steps.squad?.submitted_at}
        revised={Boolean(evaluation?.is_revision)}
        draft={squad}
        onChange={(patch) => { setFormError(''); setSquad((d) => ({ ...d, ...patch })); }}
        words={criteria.session || {}}
        hints={criteria.descriptions?.session || {}}
        ratingRange={ratingRange}
        canEdit={canEdit}
        canReopen={canReopen}
        busy={busy}
        onSubmit={() => ask('squad')}
        onReopen={() => askReopen('Reopen the squad evaluation?', 'squad')}
      />

      <div className="form-section-header esg-evaluation-workspace__div-13">
        <h3>Chair Evaluation</h3>
        <div className="section-divider"></div>
      </div>
      {workspace.chair ? (
        <RatingStep
          kind="chair"
          heading={workspace.chair.name}
          badge="CHAIR"
          available={Boolean(steps.chair?.available)}
          waitingText={`Opens once ${workspace.chair.name} has submitted this council for audit.`}
          submittedAt={steps.chair?.submitted_at}
          draft={chair}
          onChange={(patch) => { setFormError(''); setChair((d) => ({ ...d, ...patch })); }}
          words={criteria.chair || {}}
          hints={criteria.descriptions?.chair || {}}
          ratingRange={ratingRange}
          canEdit={canEdit}
          canReopen={canReopen}
          busy={busy}
          onSubmit={() => ask('chair')}
          onReopen={() => askReopen('Reopen the chair evaluation?', 'chair')}
        />
      ) : (
        <div className="no-student-placeholder">No Chair set for this council.</div>
      )}

      <div className="form-section-header esg-evaluation-workspace__div-13">
        <h3>Individual Tasks</h3>
        <div className="section-divider"></div>
      </div>
      {taskMembers.length === 0 && (
        <div className="no-student-placeholder">
          {workspace.task_applicable === false ? 'This council has no individual task.' : 'No members in this squad yet.'}
        </div>
      )}
      <div className="esg-task-grid">
      {taskMembers.map((m) => (
        <TaskCard
          key={m.student_id}
          member={m}
          draft={tasks[m.student_id] || {}}
          onChange={(key, patch) => { setFormError(''); setTasks((all) => ({ ...all, [m.student_id]: { ...(all[m.student_id] || {}), [key]: { confirmed: null, rating: null, note: '', ...((all[m.student_id] || {})[key] || {}), ...patch } } })); }}
          criteria={criteria}
          canEdit={canEdit}
          canReopen={canReopen}
          busy={busy}
          onSubmit={() => askTask(m)}
          onNotSubmitted={() => askNotSubmitted(m)}
          onReopen={() => askReopen(`Reopen ${m.name}'s task evaluation?`, 'task', m.student_id)}
        />
      ))}
      </div>

      {confirm && (
        <EsgConfirmDialog title={confirm.title} confirmLabel={confirm.label} busy={busy} onConfirm={confirm.go} onCancel={() => setConfirm(null)}>
          <p className="esg-confirm-warning">{confirm.body}</p>
        </EsgConfirmDialog>
      )}
    </div>
  );
}
